// Servicio base para consumir la API de SkyBotNMP
// Basado en el patrón de DashboardFC-v2 (fetch nativo + clase Servicio)

export class ApiServiceError extends Error {
  status: number
  errors?: Record<string, string[]>

  constructor(message: string, status: number, errors?: Record<string, string[]>) {
    super(message)
    this.name = 'ApiServiceError'
    this.status = status
    this.errors = errors
  }
}

export class Servicio {
  private readonly baseUrl: string

  constructor() {
    this.baseUrl = process.env.REACT_APP_API_URL ?? 'http://localhost:8000'
  }

  private url(ruta: string) {
    return `${this.baseUrl}${ruta}`
  }

  private headers(token?: string, conBody = false): HeadersInit {
    const h: Record<string, string> = { Accept: 'application/json' }
    if (conBody) h['Content-Type'] = 'application/json'
    if (token) h.Authorization = `Bearer ${token}`
    return h
  }

  private async parse<T>(res: Response): Promise<T> {
    const esJson = res.headers.get('content-type')?.includes('application/json')
    const data = res.status !== 204 ? (esJson ? await res.json() : await res.text()) : null

    if (!res.ok) {
      const p = data as { message?: string; errors?: Record<string, string[]> }
      throw new ApiServiceError(p?.message ?? `Error ${res.status}`, res.status, p?.errors)
    }
    return data as T
  }

  async getApi<T>(ruta: string, token = ''): Promise<T> {
    return this.parse<T>(await fetch(this.url(ruta), { headers: this.headers(token) }))
  }

  async setApi<T>(ruta: string, token = '', metodo: 'POST' | 'PUT' | 'PATCH' | 'DELETE', body?: unknown): Promise<T> {
    const isFormData = body instanceof FormData
    const headers: Record<string, string> = { Accept: 'application/json' }
    if (token) headers.Authorization = `Bearer ${token}`
    if (!isFormData && body != null) headers['Content-Type'] = 'application/json'

    return this.parse<T>(await fetch(this.url(ruta), {
      method: metodo,
      headers,
      ...(body != null ? { body: isFormData ? body : JSON.stringify(body) } : {}),
    }))
  }

  /** Hace POST y retorna el ReadableStream para consumir SSE (streaming) */
  async streamApi(ruta: string, token: string, body: unknown): Promise<Response> {
    const res = await fetch(this.url(ruta), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    })

    if (!res.ok) {
      throw new ApiServiceError(`Error ${res.status}`, res.status)
    }

    return res
  }
}
